import { Navigate, useParams, type RouteObject } from "react-router";
import { planRoute } from "../builder/routes.js";
import { todayString } from "./model/days.js";
import { weekParam } from "./paths.js";
import { Athletes } from "./views/Athletes.js";
import { Settings } from "./views/Settings.js";
import { Lifts } from "./views/Lifts.js";
import { Live } from "./views/Live.js";
import { Meets } from "./views/Meets.js";
import { Overview } from "./views/Overview.js";

/** Athletes and lifts no longer sit under a week; old links land on their own address. */
function Timeless({ view }: { view: string }) {
  const { id } = useParams();
  return <Navigate to={"/" + view + (id ? "/" + encodeURIComponent(id) : "")} replace />;
}

/** Anything that is not a page lands on its week's Overview, or this week's when the address has none. */
function ToWeek() {
  const { monday } = useParams();
  return <Navigate to={"/w/" + weekParam(monday, todayString())} replace />;
}

/**
 * DESIGN §16.6: each place is a table, and what a row opens is a panel over it at its own address,
 * so the same component serves the list and the list with a panel. The home page is the Plan (§17.6).
 */
export const portalRoutes: RouteObject[] = [
  { index: true, ...planRoute },
  { path: "athletes", element: <Athletes /> },
  { path: "athlete/:id", element: <Athletes /> },
  { path: "lifts", element: <Lifts /> },
  { path: "lift/:key", element: <Lifts /> },
  // One element over both addresses, so a meet just added keeps its dialog as its address becomes its own.
  { element: <Meets />, children: [{ path: "meets" }, { path: "meet/:id" }] },
  // One element over both addresses, so the board stays put while an athlete's workout opens beside it.
  { element: <Live />, children: [{ path: "live" }, { path: "live/:athleteID" }] },
  { path: "settings", element: <Settings /> },
  { path: "sessions", element: <ToWeek /> },
  { path: "*", element: <ToWeek /> },
  {
    path: "w/:monday",
    children: [
      { index: true, element: <Overview /> },
      // DESIGN §33: a session and a line on a phone; the Plan page reads them, so it stays mounted between them.
      { path: "plan", ...planRoute, children: [{ path: "d/:label" }, { path: "d/:label/l/:n" }] },
      { path: "day/:date", element: <Overview /> },
      { path: "session/:id", element: <Overview /> },
      { path: "track/:key", element: <Overview /> },
      { path: "sessions", element: <ToWeek /> },
      { path: "athletes", element: <Timeless view="athletes" /> },
      { path: "athlete/:id", element: <Timeless view="athlete" /> },
      { path: "lifts", element: <Timeless view="lifts" /> },
      { path: "lift/:id", element: <Timeless view="lift" /> },
      { path: "*", element: <ToWeek /> },
    ],
  },
];
